(() => {
  const initPackage = (root) => {
    if (root.dataset.gmuPackageReady === 'true') return;
    root.dataset.gmuPackageReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-gmu-package-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-gmu-package-panel]'));
    const tabsNav = root.querySelector('[data-gmu-package-tabs-nav]');
    const tabsViewport = root.querySelector('[data-gmu-package-tabs-viewport]');
    const tabsTrack = root.querySelector('[data-gmu-package-tabs-track]');
    const tabsPrevBtn = root.querySelector('[data-gmu-package-tabs-prev]');
    const tabsNextBtn = root.querySelector('[data-gmu-package-tabs-next]');

    let tabsMinX = 0;
    let tabsCurrentX = 0;
    let tabsSuppressClick = false;
    let resizeTimer = null;

    const tabsDrag = {
      pointerId: null,
      startX: 0,
      lastX: 0,
      startTranslate: 0,
      moved: false,
    };

    const clampTabsX = (x) => Math.max(tabsMinX, Math.min(0, x));

    const updateTabsOverflowState = () => {
      if (!tabsNav) return;
      const hasOverflow = tabsMinX < 0;
      tabsNav.classList.toggle('is-overflowing', hasOverflow);
      tabsNav.classList.toggle('has-overflow-left', hasOverflow && tabsCurrentX < -1);
      tabsNav.classList.toggle('has-overflow-right', hasOverflow && tabsCurrentX > tabsMinX + 1);
    };

    const setTabsX = (x, animate = false) => {
      if (!tabsTrack) return;
      tabsCurrentX = clampTabsX(x);
      tabsTrack.style.transition = animate ? 'transform 320ms ease' : 'none';
      tabsTrack.style.transform = `translate3d(${tabsCurrentX}px, 0, 0)`;
      updateTabsOverflowState();
    };

    const refreshTabsBounds = () => {
      if (!tabsViewport || !tabsTrack) return;
      const overflow = tabsTrack.scrollWidth - tabsViewport.clientWidth;
      tabsMinX = overflow > 1 ? -overflow : 0;
      setTabsX(tabsMinX === 0 ? 0 : tabsCurrentX);
    };

    const scheduleTabsRefresh = () => {
      refreshTabsBounds();
      requestAnimationFrame(refreshTabsBounds);
      window.setTimeout(refreshTabsBounds, 80);
    };

    const ensureTabVisible = (tab, animate = true) => {
      if (!tab || !tabsViewport || !tabsTrack || tabsMinX === 0) return;
      const viewportRect = tabsViewport.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const styles = window.getComputedStyle(tabsNav || tabsViewport);
      const edgeSize = Number.parseFloat(styles.getPropertyValue('--gmu-package-tabs-edge-size')) || 44;
      const safeInset = edgeSize + 4;
      let nextX = tabsCurrentX;

      if (tabRect.left < viewportRect.left + safeInset) {
        nextX += viewportRect.left + safeInset - tabRect.left;
      } else if (tabRect.right > viewportRect.right - safeInset) {
        nextX -= tabRect.right - (viewportRect.right - safeInset);
      }

      if (Math.abs(nextX - tabsCurrentX) > 0.5) setTabsX(nextX, animate);
    };

    const setTab = (tabId) => {
      let activeTab = null;
      tabs.forEach((tab) => {
        const active = tab.dataset.gmuPackageTab === tabId;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) activeTab = tab;
      });

      panels.forEach((panel) => {
        const active = panel.dataset.gmuPackagePanel === tabId;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      ensureTabVisible(activeTab, true);
    };

    const normalizeTabsWheelDelta = (event) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaX * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaX * tabsViewport.clientWidth;
      return event.deltaX;
    };

    const dispatchAddToCartTracking = (button, product) => {
      document.dispatchEvent(new CustomEvent('ga4:add_to_cart', {
        detail: {
          button,
          product,
          quantity: 1,
        },
      }));
    };

    const addVariantToCart = async (button) => {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1,
          }),
        });
        const state = await response.json();
        if (!response.ok) throw new Error(state.description || state.message || 'Unable to add item to cart.');

        document.dispatchEvent(new CustomEvent('cart:refresh'));
        const drawer = document.getElementById('Cart-Drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
        else document.getElementById('cart-drawer-toggle')?.click();
        dispatchAddToCartTracking(button, state);
      } catch (error) {
        console.warn('[gmu-package] add to cart failed', error);
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => setTab(tab.dataset.gmuPackageTab));
    });

    tabsPrevBtn?.addEventListener('click', () => {
      if (!tabsViewport) return;
      setTabsX(tabsCurrentX + tabsViewport.clientWidth * 0.65, true);
    });

    tabsNextBtn?.addEventListener('click', () => {
      if (!tabsViewport) return;
      setTabsX(tabsCurrentX - tabsViewport.clientWidth * 0.65, true);
    });

    tabsViewport?.addEventListener(
      'wheel',
      (event) => {
        if (tabsMinX === 0) return;
        const deltaX = normalizeTabsWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault();
        setTabsX(tabsCurrentX - deltaX);
      },
      { passive: false }
    );

    tabsViewport?.addEventListener('pointerdown', (event) => {
      if (tabsMinX === 0 || (event.pointerType === 'mouse' && event.button !== 0)) return;
      tabsSuppressClick = false;
      tabsDrag.pointerId = event.pointerId;
      tabsDrag.startX = event.clientX;
      tabsDrag.lastX = event.clientX;
      tabsDrag.startTranslate = tabsCurrentX;
      tabsDrag.moved = false;
      tabsTrack.style.transition = 'none';
      tabsViewport.setPointerCapture?.(event.pointerId);
    });

    tabsViewport?.addEventListener('pointermove', (event) => {
      if (tabsDrag.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - tabsDrag.startX;
      tabsDrag.lastX = event.clientX;
      if (!tabsDrag.moved && Math.abs(deltaX) > 8) tabsDrag.moved = true;
      if (!tabsDrag.moved) return;
      event.preventDefault();
      setTabsX(tabsDrag.startTranslate + deltaX);
    });

    const endTabsDrag = (event) => {
      if (tabsDrag.pointerId !== event.pointerId) return;
      tabsViewport?.releasePointerCapture?.(event.pointerId);
      if (tabsDrag.moved) {
        tabsSuppressClick = true;
        window.setTimeout(() => {
          tabsSuppressClick = false;
        }, 180);
      }
      tabsDrag.pointerId = null;
      tabsDrag.moved = false;
      setTabsX(tabsCurrentX);
    };

    tabsViewport?.addEventListener('click', (event) => {
      if (!tabsSuppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
    tabsViewport?.addEventListener('pointerup', endTabsDrag);
    tabsViewport?.addEventListener('pointercancel', endTabsDrag);
    tabsTrack?.addEventListener('dragstart', (event) => event.preventDefault());

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-gmu-package-add]');
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      addVariantToCart(button);
    });

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        scheduleTabsRefresh();
        ensureTabVisible(tabs.find((tab) => tab.classList.contains('is-active')), false);
      }, 120);
    });

    scheduleTabsRefresh();
    if (tabs[0]) setTab(tabs[0].dataset.gmuPackageTab);
    window.addEventListener('load', scheduleTabsRefresh, { once: true });
    document.fonts?.ready?.then(scheduleTabsRefresh).catch(() => {});
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-gmu-package]').forEach(initPackage);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
